import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import { useCallback, useEffect } from "react";
import { CssBaseline } from "@mui/material";
import { ThemeModeProvider } from "./ThemeModeContext";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { RobotPictureProvider } from "./context/RobotPictureContext";
import { BotTypePictureProvider } from "./context/BotTypePictureContext";
import ProtectedRoute from "./auth/ProtectedRoute";
import MainLayout from "./components/layout/MainLayout";
import useUserStatusSocket from "./hooks/useUserStatusSocket";
import { joinPortalUserRoom } from "./services/socketService";

// Auth Pages
import LoginPage from "./pages/Login/Login";
import RegisterPage from "./pages/Register/Register";
import Terms from "./pages/Terms/Terms";

// Protected Pages
import Dashboard from "./pages/Dashboard/Dashboard";
import Profile from "./pages/Profile/Profile";
import Advertisements from "./pages/Advertisements/Advertisements";
import AdvertisementDetails from "./pages/Advertisements/AdvertisementDetails";
import MyRobots from "./pages/Robots/MyRobots";
import RobotDetails from "./pages/Robots/RobotDetails";
import Venue from "./pages/Venue/Venue";
import Maps from "./pages/Maps/Maps";
import MapDetail from "./pages/Maps/MapDetail";
import Notifications from "./pages/Notifications/Notifications";
import Settings from "./pages/Settings/Settings";
import Payment from "./pages/Payment/Payment";
import Privacy from "./pages/Privacy/Privacy";
import NotFound from "./pages/NotFound/NotFound";
import RoomNumberPageAero from "./pages/delivery/RoomNumberPageAero";
import PatrolPage from "./pages/patrol/PatrolPage";

const router = createBrowserRouter([
  // Public routes
  { path: "/login", element: <LoginPage /> },
  { path: "/register", element: <RegisterPage /> },
  { path: "/terms", element: <Terms /> },

  // Protected routes
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <MainLayout />,
        children: [
          { path: "/dashboard", element: <Dashboard /> },
          { path: "/profile", element: <Profile /> },
          { path: "/advertisements", element: <Advertisements /> },
          { path: "/advertisements/:id", element: <AdvertisementDetails /> },
          { path: "/robots", element: <MyRobots /> },
          { path: "/robots/:id", element: <RobotDetails /> },
          { path: "/venue", element: <Venue /> },
          { path: "/maps", element: <Maps /> },
          { path: "/maps/:id", element: <MapDetail /> },
          { path: "/notifications", element: <Notifications /> },
          { path: "/settings", element: <Settings /> },
          { path: "/account", element: <Navigate to="/profile" replace /> },
          { path: "/payment", element: <Payment /> },
          { path: "/privacy", element: <Privacy /> },
          { path: "/robot-tracker", element: <RoomNumberPageAero /> },
          { path: "/patrol", element: <PatrolPage /> },
        ],
      },
    ],
  },

  // Redirects
  { path: "/", element: <Navigate to="/login" replace /> },
  { path: "/notfound", element: <NotFound /> },
  { path: "*", element: <Navigate to="/notfound" replace /> },
]);

// Inner wrapper — needs to be inside AuthProvider to access useAuth
const AppInner = () => {
  const { logout, user } = useAuth();

  // Register this socket connection in the user's room so the backend can send
  // targeted events (USER_APPROVED, USER_SUSPENDED, USER_ACCESS_UPDATED…). The v1
  // login returns both `id` and `_id` (= usersv1 _id, preserved === legacy _id),
  // so the room key is unchanged; we read `_id` first and fall back to `id`.
  const userRoomId = user?._id || user?.id;
  useEffect(() => {
    if (userRoomId) {
      joinPortalUserRoom(userRoomId);
    }
  }, [userRoomId]);

  const handleForceLogout = useCallback(() => {
    logout();
    window.location.replace("/login");
  }, [logout]);

  useUserStatusSocket(handleForceLogout);
  return <RouterProvider router={router} />;
};

function App() {
  return (
    <ThemeModeProvider>
      <CssBaseline />
      <AuthProvider>
        <RobotPictureProvider>
          <BotTypePictureProvider>
            <AppInner />
          </BotTypePictureProvider>
        </RobotPictureProvider>
      </AuthProvider>
    </ThemeModeProvider>
  );
}

export default App;
